import { CsvTemplate, Expense, ExpenseStatusEnum, ExportFormatEnum, TripFolder } from "@finway-group/shared/lib/models"
import { TFunction } from "i18next"

import i18n from "Shared/locales/i18n"
import { NotificationService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"

import { MultiActionConfirmModal } from "./expenseDetails/folderDetails/multiActionConfirm.modal"
import { isFolderExpense } from "./expenseUtils"

export const FOLDER_CHILDREN_LIMIT = 20

export const getFolderMaximumPossibleStatus = (folder: TripFolder, children: Array<Expense>) =>
    children.reduce((min: ExpenseStatusEnum, expense: Expense) => {
        if (expense.status === ExpenseStatusEnum.DECLINED || min === ExpenseStatusEnum.DECLINED) return ExpenseStatusEnum.DECLINED

        return expense.status < min ? expense.status : min
    }, folder.status)

export const canFolderAdvance = (folder: TripFolder, subExpenses: Array<Expense>) => {
    const childrenStatus = getFolderMaximumPossibleStatus(folder, subExpenses)

    if (childrenStatus === ExpenseStatusEnum.DECLINED) return false

    return folder.status <= childrenStatus
}

export const isFolderAttachableAndDetachable = (folder: TripFolder) => folder.status < ExpenseStatusEnum.REVIEWED && folder.status !== ExpenseStatusEnum.DECLINED

export const getProcessableSubExpenses = (folder: TripFolder, subExpenses: Array<Expense>) => subExpenses.filter((expense) => folder.status === expense.status)

export const getNextStatus = (status: ExpenseStatusEnum) => {
    switch (status) {
        case ExpenseStatusEnum.DOCS_NEEDED:
            return ExpenseStatusEnum.INVOICE_PENDING
        case ExpenseStatusEnum.INVOICE_PENDING:
            return ExpenseStatusEnum.APPROVED
        case ExpenseStatusEnum.APPROVED:
            return ExpenseStatusEnum.REVIEWED
        case ExpenseStatusEnum.REVIEWED:
            return ExpenseStatusEnum.PAID
        case ExpenseStatusEnum.PAID:
            return ExpenseStatusEnum.DONE
        default:
            return undefined
    }
}

export const getFolderProgressionInfo = (folder: TripFolder, subExpenses: Array<Expense>) => {
    const canAdvance = canFolderAdvance(folder, subExpenses)
    const processableSubExpenses = getProcessableSubExpenses(folder, subExpenses)
    const nextStatus = getNextStatus(folder.status)
    const currentStatus = folder.status

    return { canAdvance, processableSubExpenses, currentStatus, nextStatus }
}

export const getFolderActionStrings = (folder: TripFolder, t: TFunction) => {
    const statusString = folder.isDraft ? "draft" : ExpenseStatusEnum[folder.status].toLowerCase()
    return {
        title: t(`info:folder.status.${statusString}.title`),
        description: t(`info:folder.status.${statusString}.description`),
        tooltip: t(`info:folder.status.${statusString}.tooltip`),
        confirm: t(`action:request.folder.status.${statusString}.confirm`),
        reject: t(`action:request.folder.status.${statusString}.reject`),
    }
}

export const isFolderResettable = (folder: TripFolder) =>
    [ExpenseStatusEnum.APPROVED, ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(folder.status)

export const handleFolderExportAction = async (
    activeExpense: Expense,
    subExpenses: Array<Expense>,
    showModal: (component: any, destroy: boolean, props: any) => void,
    onExport: (expenses: Array<Expense>, exportFormat: ExportFormatEnum, csvTemplate?: CsvTemplate) => void,
    callbackExpenses: Array<Expense>,
    exportFormat: ExportFormatEnum,
    csvTemplate?: CsvTemplate
) => {
    if (!isFolderExpense(activeExpense)) {
        return onExport(callbackExpenses, exportFormat, csvTemplate)
    }

    // See if any of the subexpenses has been exported to the specific format
    // e.g. if the export format is SEPA, exportedSubExpenses should have all expenses with exportInformation.isExportedAsSepa === true
    const exportedSubExpenses = subExpenses.filter((subExpense) => subExpense.hasBeenExportedTo(exportFormat))
    const unexportedSubExpenses = subExpenses.filter((subExpense) => !exportedSubExpenses.includes(subExpense))

    const onConfirm = (key: string) => {
        // determine which to export
        switch (key) {
            case "not_exported":
                if (unexportedSubExpenses.length === 0) {
                    // When no unexported expense remaining, cancel the action.
                    const noExportableExpensesMessage = i18n.exists(`info:folder.status.paid.modal.format.${exportFormat}`)
                        ? i18n.t("info:folder.status.paid.modal.already_exported.message_format", { format: i18n.t(`info:folder.status.paid.modal.format.${exportFormat}`) })
                        : i18n.t("info:folder.status.paid.modal.already_exported.message")
                    NotificationService.send(NotificationTypeEnum.ERROR, i18n.t("error:error"), noExportableExpensesMessage)
                    return
                }
                onExport([activeExpense, ...unexportedSubExpenses], exportFormat, csvTemplate)
                return
            case "all":
            default:
                onExport([activeExpense, ...subExpenses], exportFormat, csvTemplate)
        }
    }

    if (exportFormat === ExportFormatEnum.MARK_AS_DONE) {
        return onExport(
            // Only pass in the folder + subexpenses that are in the right state to be marked as done
            [activeExpense, ...subExpenses].filter((expense) => expense.status === ExpenseStatusEnum.PAID),
            exportFormat,
            csvTemplate
        )
    }

    if (exportedSubExpenses.length > 0) {
        // When a specific format has a translation string, we use the sentence with the format. Else, we use just the generic sentence.
        const description = i18n.exists(`info:folder.status.paid.modal.format.${exportFormat}`)
            ? i18n.t("info:folder.status.paid.modal.description_1_format", { format: i18n.t(`info:folder.status.paid.modal.format.${exportFormat}`) })
            : i18n.t("info:folder.status.paid.modal.description_1")

        // If any subexpense has been exported, show the message
        showModal(MultiActionConfirmModal, true, {
            isShowing: true,
            onConfirm,
            confirmOptions: [
                { action: "not_exported", text: i18n.t("info:folder.status.paid.modal.only_not_exported") },
                { action: "all", text: i18n.t("info:folder.status.paid.modal.export_all") },
            ],
            okButtonText: i18n.t("info:folder.status.paid.modal.confirm"),
            cancelButtonText: i18n.t("info:folder.status.paid.modal.cancel"),
            title: i18n.t("info:folder.status.paid.modal.title"),
            description: description + i18n.t("info:folder.status.paid.modal.description_2"),
        })
    } else {
        // If none has been exported yet, execute the export directl
        onConfirm("all")
    }
}

export const separateFoldersAndIndividualExpenses = (expenses: Array<any>) => {
    const folders: Array<TripFolder> = []
    const individualExpenses: Array<Expense> = []
    for (const expense of expenses) {
        if (isFolderExpense(expense)) folders.push(expense)
        else individualExpenses.push(expense)
    }
    return { folders, individualExpenses }
}

export const getExpensesThatDontBelongToFolders = (expenses: Array<any>) => {
    const { folders, individualExpenses } = separateFoldersAndIndividualExpenses(expenses)
    const folderMap = new Map<string, TripFolder>(folders.map((folder) => [folder.id, folder]))

    return { expensesWithoutFolder: individualExpenses.filter((expense) => !expense.folderId || !folderMap.get(expense.folderId)), folders }
}

export const isFolderPayable = (folder: TripFolder, subExpenses: Array<Expense>) => {
    if (folder.status !== ExpenseStatusEnum.REVIEWED) return false
    const payableSubExpenses = subExpenses.filter((subExpense) => subExpense.status === ExpenseStatusEnum.REVIEWED && subExpense.totalGrossPrice > 0)
    return payableSubExpenses.length > 0
}

export const isFolderMarkableAsPaid = (folder: TripFolder, subExpenses: Array<Expense>) => {
    if (isFolderPayable(folder, subExpenses)) return true

    if (folder.status !== ExpenseStatusEnum.REVIEWED) return false

    const allSubExpensesAreAhead = subExpenses.every((subExpense) => subExpense.status > ExpenseStatusEnum.REVIEWED)
    return allSubExpensesAreAhead
}
